// Canonical sharing-UI template — anonymous capability-link viewer.
// The token in the URL is the credential, so this renders with zero auth state and
// its own minimal chrome. A <meta name="referrer" content="no-referrer"> is injected
// while mounted so the token never leaves in a Referer header (SHARING_PLAN §1.6.6).
// (Apps should also set Referrer-Policy: no-referrer as a real response header.)

import React, { useEffect, useState } from "react";
import { SharingApiError, type SharingClient, type LinkViewResponse } from "./types";
import type { ArtifactRenderer } from "./common";

export interface LinkViewProps {
  token: string;
  client: SharingClient;
  renderArtifact: ArtifactRenderer;
  /** App/product name shown in the minimal header. */
  productName?: string;
}

export const LinkView: React.FC<LinkViewProps> = ({ token, client, renderArtifact, productName = "StructuraMatrix" }) => {
  const [data, setData] = useState<LinkViewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setData(await client.getLink(token));
      } catch (err) {
        if (err instanceof SharingApiError) setStatus(err.status);
        setError(err instanceof Error ? err.message : "Kunne ikke hente delt beregning.");
      }
    })();
  }, [client, token]);

  const errorHeading = status === 410 ? "Lenken er utløpt eller beregningen er fjernet" : "Ugyldig lenke";

  return (
    <div className="sm-app">
      <header style={{ background: "var(--sm-surface, #fff)", borderBottom: "1px solid var(--sm-border, #e2e8f0)", padding: "0.75rem 0" }}>
        <div className="sm-container" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <strong>{productName}</strong>
          <span style={{ color: "var(--sm-text-muted, #64748b)", fontSize: "0.9rem" }}>delt beregning (kun visning)</span>
        </div>
      </header>
      <div className="sm-page">
        <div className="sm-container">
          {error && (
            <div className="sm-error-box" style={{ marginTop: "1.5rem" }}>
              <strong>{errorHeading}</strong>
              <pre className="sm-error-details">{error}</pre>
            </div>
          )}

          {data && (
            <>
              <div className="sm-page-header">
                <h1><i className="bi bi-link-45deg"></i> {data.meta.title}</h1>
                <p>Delt via lenke · kun visning{data.meta.subtitle && <> · {data.meta.subtitle}</>}</p>
              </div>
              <div className="sm-card">
                <div className="sm-card-body">{renderArtifact(data.artifact, data.resourceType)}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkView;
